import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { usePortalSlug } from "@/contexts/PortalDomainContext";
import api from "@/lib/api";
import { loadTenantFont, resolveLogoUrl } from "@/lib/branding";
import { Recycle, ArrowLeft, Info } from "@phosphor-icons/react";
import PortalFooter from "@/components/PortalFooter";

export default function PortalLegalPage({ page }) {
  const slug = usePortalSlug();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get(`/portal/${slug}/legal/${page}`).then((r) => {
      setData(r.data);
      const b = r.data.tenant?.branding || {};
      document.documentElement.style.setProperty("--tenant-primary", b.primary_color || "#2A86A8");
      document.documentElement.style.setProperty("--tenant-secondary", b.secondary_color || "#3FA64E");
      loadTenantFont(b.font_family);
      document.title = `${r.data.content.title} — ${r.data.tenant.name}`;
    }).catch((e) => setErr(e.response?.data?.detail || "Seite nicht gefunden"));
  }, [slug, page]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="text-center">
        <h1 className="font-heading text-3xl font-bold mb-2">404</h1>
        <p className="text-zinc-500 mb-4">{err}</p>
        <Link to="/" className="underline text-sm">← Zur Tonneva Startseite</Link>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center">Lade…</div>;

  const { tenant, content } = data;
  const b = tenant.branding || {};
  const primary = b.primary_color || "#2A86A8";
  const complete = tenant.master_data_complete;

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "var(--tenant-font)" }} data-testid={`portal-legal-${page}`}>
      <header className="border-b border-black/5">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to={`/portal/${slug}`} className="flex items-center gap-3">
            {b.logo_url ? (
              <img src={resolveLogoUrl(b.logo_url)} alt={tenant.name} className="h-8" />
            ) : (
              <div className="w-9 h-9 rounded-md flex items-center justify-center" style={{ background: primary }}>
                <Recycle size={18} weight="bold" className="text-white" />
              </div>
            )}
            <div>
              <div className="font-bold tracking-tight">{tenant.name}</div>
              <div className="text-[10px] text-zinc-500">{tenant.city}</div>
            </div>
          </Link>
          <Link to={`/portal/${slug}`} className="text-xs font-medium px-3 py-2 rounded-md hover:bg-zinc-100 text-zinc-600 inline-flex items-center gap-1">
            <ArrowLeft size={12} weight="bold" /> Zurück
          </Link>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-14 w-full">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-bold rounded-full mb-4" style={{ background: `${primary}15`, color: primary }}>
            Rechtliches
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight" data-testid="legal-title">
            {content.title}
          </h1>
          {!complete && page === "impressum" && (
            <div className="mt-4 p-3 rounded bg-amber-50 border border-amber-200 text-xs text-amber-800 flex items-start gap-2" data-testid="legal-incomplete-notice">
              <Info size={14} weight="fill" className="flex-shrink-0 mt-0.5" />
              <div>Der Mandant hat die Firmen-Stammdaten noch nicht vollständig hinterlegt. Bitte den Betreiber kontaktieren, wenn Sie Angaben vermissen.</div>
            </div>
          )}
        </div>

        <div className="space-y-8">
          {content.sections.map((s, i) => (
            <section key={s.heading || i} data-testid={`legal-section-${i}`}>
              <h2 className="font-heading text-xl font-bold tracking-tight mb-3">{s.heading}</h2>
              {s.lines && (
                <dl className="space-y-1 text-sm">
                  {s.lines.map((l) => (
                    <div key={`${l.label || "x"}:${l.value}`} className="flex gap-3">
                      {l.label && <dt className="text-zinc-500 min-w-[140px]">{l.label}</dt>}
                      <dd className={l.label ? "text-zinc-800" : "text-zinc-800 leading-relaxed"}>{l.value}</dd>
                    </div>
                  ))}
                </dl>
              )}
              {s.paragraphs && s.paragraphs.map((p) => (
                <p key={p.slice(0, 40)} className="text-sm text-zinc-700 leading-relaxed mb-2 whitespace-pre-line">{p}</p>
              ))}
            </section>
          ))}
        </div>
      </main>

      <PortalFooter tenant={tenant} slug={slug} primary={primary} />
    </div>
  );
}
